import { useState } from "react";
import { toast } from "sonner";
import { Heart, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

/**
 * Warm modal to compose a 2-3 sentence testimony. Soft cap ~500 chars.
 * Save routes through POST /api/faith-journey/testimonies with an optional
 * context tag (forward/leading_group/general) so the Praise Report can
 * lightly categorize entries.
 */
const MAX = 500;

export default function ShareTestimonyModal({ open, onClose, context = "general", onSaved }) {
  const [text, setText] = useState("");
  const [consent, setConsent] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    const clean = text.trim();
    if (clean.length < 4) {
      toast.error("Add a sentence or two so we can celebrate with you.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/faith-journey/testimonies", {
        text: clean,
        context,
        consent_praise_report: consent,
      });
      toast.success("Praise God — Story Shared!");
      setText("");
      setConsent(false);
      onSaved?.(data);
      onClose?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't save that story.");
    } finally { setSaving(false); }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-charcoal-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      data-testid="share-testimony-modal"
      onClick={onClose}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="patria-card w-full max-w-lg p-6 sm:p-8 space-y-4"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-full bg-terracotta-500/15 text-terracotta-600 flex items-center justify-center shrink-0">
              <Heart className="h-5 w-5" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-terracotta-500 font-medium">
                Faith Journey · Share Your Story
              </p>
              <h2 className="font-serif text-2xl text-forest-900 mt-1">
                Tell Us What God Did
              </h2>
              <p className="text-xs text-charcoal-muted mt-1">
                Two or three warm sentences. This is a celebration — the room is safe.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="patria-btn-ghost text-charcoal-muted"
            data-testid="share-testimony-close"
          >
            <X className="h-5 w-5" strokeWidth={1.5} />
          </button>
        </div>
        <textarea
          className="patria-input w-full min-h-[140px]"
          maxLength={MAX + 20}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="What happened? What did you see God do? Where are you today?"
          autoFocus
          data-testid="share-testimony-textarea"
        />
        <div className="flex items-center justify-between text-xs text-charcoal-muted">
          <span data-testid="share-testimony-char-count">
            {text.length} / {MAX} characters
          </span>
          <span className="italic">Soft cap {MAX} chars.</span>
        </div>
        <label className="flex items-start gap-2 text-xs text-charcoal-soft cursor-pointer" data-testid="share-testimony-consent-label">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            className="mt-0.5 rounded"
            data-testid="share-testimony-consent"
          />
          <span>
            <strong>Include in the Praise Report.</strong> Testimonies may be
            included in aggregated Praise Reports viewed and exported by
            People Operations. If you'd like to keep this story private,
            leave this unchecked — it will still live in your own Faith Journey.
          </span>
        </label>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="patria-btn-secondary"
            data-testid="share-testimony-cancel"
          >
            Not Right Now
          </button>
          <button
            type="submit"
            disabled={saving || text.trim().length < 4}
            className="patria-btn-primary"
            data-testid="share-testimony-submit"
          >
            <Heart className="h-4 w-4 mr-1" strokeWidth={1.5} />
            {saving ? "Sharing…" : "Share My Story"}
          </button>
        </div>
      </form>
    </div>
  );
}
